// assets
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import DashboardOutlined from '@ant-design/icons/DashboardOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// icons
const icons = {
  AppstoreOutlined,
  DashboardOutlined,
  MessageOutlined,
  UserOutlined
};

// ==============================|| MENU ITEMS - CAREGIVER ||============================== //

/**
 * The caregiver's sidebar.
 *
 * WHY A TREE AND NOT THE GRANT-BUILT MENU
 * A grant-built menu takes its URLs from the catalogue, which stores ONE route per page —
 * the supervisor-centric one. That is how a caregiver came to be navigating
 * `/supervisor/child`: correct capability, wrong persona's URL, and a screen scoped to the
 * whole organization behind it. A persona with its own route prefix has to render from its
 * own tree, which is the same reason the therapist and supervisor menus exist.
 *
 * THREE GATES STILL APPLY, and none of them is bypassed by having a tree:
 *   catalogueCode  the Features and Pages switch (rbac_app_page.code) AND the per-role
 *                  narrowing from Role Definitions — filterMenuByGrants shows a coded item
 *                  iff its code is granted, so un-ticking it here hides it.
 *   feature        the organization's PRODUCT TIER entitlement code
 *                  (GET /organizations/my-features). Same code space adminLevels.js uses.
 *                  An item with no `feature` is never tier-gated.
 * The codes are PAGE codes, not feature codes, so a single sub-option can be switched off;
 * the backend also reports every page of a switched-off feature as disabled, so the
 * feature-level switch is covered by the same match.
 *
 * THE DASHBOARD has a page of its own, `caregiver_dashboard`. The Caregiver role is granted
 * Dashboard & Reports, but that feature's three pages are the admin dashboard, the
 * supervisor dashboard and the supervisor reports — none of them a parent's screen, and its
 * route_prefix is `/admin/dashboard`, which the role is explicitly DENIED. So the grant used
 * to put an item in the sidebar that bounced on every click. It now points at a screen that
 * is theirs.
 */
const caregiver = {
  id: 'group-caregiver',
  title: 'Caregiver',
  icon: icons.UserOutlined,
  type: 'group',
  children: [
    {
      id: 'caregiver-dashboard',
      title: 'Dashboard',
      type: 'item',
      url: '/caregiver/dashboard',
      icon: icons.DashboardOutlined,
      breadcrumbs: false,
      catalogueCode: 'caregiver_dashboard',
      feature: 'dashboard_reports'
    },
    {
      id: 'caregiver-communication-diary',
      title: 'Communication Diary',
      type: 'item',
      url: '/caregiver/communication-diary',
      icon: icons.MessageOutlined,
      breadcrumbs: false,
      catalogueCode: 'caregiver_communication_diary',
      feature: 'communication_diary'
    },
    {
      // Activity Center — home practice assigned to the caregiver's own child. The screen
      // is not built yet (a pending feature platform-wide; the admin persona shows it the
      // same way), so the URL points at the shared "Coming Soon" placeholder rather than a
      // dead route. It is wired through the SAME three gates as every other item:
      //   catalogueCode  the Features and Pages switch AND the per-role Role Definitions
      //                  tick — filterMenuByGrants shows this item iff `activity_center` is
      //                  granted, so un-ticking Activity Center for the Caregiver role hides
      //                  it and re-ticking reveals it. This is what makes the toggle reflect.
      //   feature        the organization's product-tier entitlement.
      // Drop the placeholder url and add it to CAREGIVER_CATALOGUE_ROUTES when the real
      // screen lands.
      id: 'caregiver-activity-center',
      title: 'Activity Center',
      type: 'item',
      url: '/caregiver/activity-center',
      icon: icons.AppstoreOutlined,
      breadcrumbs: false,
      catalogueCode: 'activity_center',
      feature: 'activity_center'
    }
  ]
};

/**
 * Caregiver URLs that are NOT menu items, paired with the catalogue page they serve.
 *
 * catalogueRoutes.js collects URL↔code pairs from the menu trees so CatalogueGuard can
 * bounce a role-prefixed URL when the code behind it is switched off. A screen reached
 * in-context — a child's profile — has no menu item, so it has to be listed by hand or
 * switching `child_detail` off in Features and Pages would leave `/caregiver/child/:childId`
 * reachable by typing it. Same job ADMIN_CATALOGUE_ROUTES does for the admin menu.
 */
export const CAREGIVER_CATALOGUE_ROUTES = [
  { url: '/caregiver/child/:childId', catalogueCode: 'child_detail' },
  // The read-only IEP version view the IEP tab navigates to. Follows the IEP sub-option,
  // so switching that off closes the screen behind the tab as well as the tab.
  { url: '/caregiver/child/:childId/iep/version/:versionId', catalogueCode: 'child_iep' }
];

export default caregiver;
